import React, { useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

// Custom audio player — the native <audio controls> looks inconsistent across themes/browsers.
export default function AudioPlayer({ src, isDarkMode }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else audio.play();
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
  };

  const formatTime = (secs) => {
    if (!Number.isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${isDarkMode ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-white'}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
      />
      <button
        type="button"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause playback' : 'Play playback'}
        className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 transition-colors ${
          isDarkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
        }`}
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
      </button>

      <div
        onClick={handleSeek}
        className={`flex-1 h-1.5 rounded-full cursor-pointer overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}
      >
        <div
          className={`h-full rounded-full ${isDarkMode ? 'bg-primary-400' : 'bg-primary-500'}`}
          style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
        />
      </div>

      <span className={`text-[11px] tabular-nums flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  );
}
